import React, { useLayoutEffect, useRef, useState } from "react";

const BOTTOM_THRESHOLD_PX = 2;

const isAtBottom = (node: HTMLDivElement) =>
  node.scrollHeight - node.scrollTop - node.clientHeight <= BOTTOM_THRESHOLD_PX;

/** Keeps a growing chat pinned until the reader deliberately scrolls away. */
export const useChatAutoScroll = (contentRevision: unknown, enabled = true, resetKey?: unknown) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const attachedRef = useRef(true);
  const [detached, setDetached] = useState(false);
  const touchYRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef(0);

  const setAttached = (attached: boolean) => {
    if (attachedRef.current === attached) return;
    attachedRef.current = attached;
    setDetached(!attached);
  };

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (node && enabled && attachedRef.current) node.scrollTop = node.scrollHeight;
  }, [contentRevision, enabled]);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    attachedRef.current = true;
    setDetached(false);
    if (node && enabled) {
      node.scrollTop = node.scrollHeight;
      lastScrollTopRef.current = node.scrollTop;
    }
  }, [resetKey]);

  const onScroll = () => {
    const node = scrollRef.current;
    if (!node) return;
    const scrollingDown = node.scrollTop > lastScrollTopRef.current;
    lastScrollTopRef.current = node.scrollTop;

    if (attachedRef.current) {
      if (!isAtBottom(node)) setAttached(false);
    } else if (scrollingDown && isAtBottom(node)) {
      setAttached(true);
    }
  };

  // Detach before the next streamed render can arrive. Relying only on the
  // scroll event leaves a small race between a wheel/touch gesture and a token.
  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (event.deltaY < 0) setAttached(false);
  };

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchYRef.current = event.touches[0]?.clientY ?? null;
  };

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    const y = event.touches[0]?.clientY;
    if (y !== undefined && touchYRef.current !== null && y > touchYRef.current) setAttached(false);
    touchYRef.current = y ?? null;
  };

  const scrollToBottom = () => {
    const node = scrollRef.current;
    if (!node) return;
    setAttached(true);
    node.scrollTop = node.scrollHeight;
    lastScrollTopRef.current = node.scrollTop;
  };

  return {
    scrollRef,
    detached,
    scrollProps: { onScroll, onWheel, onTouchStart, onTouchMove },
    scrollToBottom,
  };
};

/** Floating cue shown while a live answer continues below the viewport. */
export const DetachedStreamingIndicator = ({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="absolute bottom-3 left-1/2 z-10 flex h-9 w-9 -translate-x-1/2 items-center justify-center gap-0.5 rounded-full border border-outline bg-surface/95 shadow-md backdrop-blur transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
  >
    {[0, 150, 300].map(delay => (
      <span
        key={delay}
        className="h-1.5 w-1.5 animate-pulse rounded-full bg-text-dim"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </button>
);
